import React, { useState, useEffect, useMemo, useRef } from "react";
import { Eye, Check, X, Search, Filter, Calendar, User, FileText, AlertTriangle, Clock, TrendingUp, ChevronDown } from "lucide-react";
import Topbar from "../../components/TopbarNav";        
import { 
  listComplianceReviewPlans, 
  getPlanById, 
  submitComplianceDecision, 
  handleApiError,
  getDashboardStats 
} from "../../api/procurementPlanApi";
import HOSidebar from "../../components/head-of-Operations/HOSidebar";

// Card Components (unchanged)
const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-xl shadow-sm border ${className}`}>{children}</div>
);
const CardHeader = ({ children, className = "" }) => <div className={`p-4 border-b ${className}`}>{children}</div>;
const CardContent = ({ children, className = "" }) => <div className={`p-4 ${className}`}>{children}</div>;
const CardTitle = ({ children }) => <h2 className="font-semibold text-lg">{children}</h2>;
const CardDescription = ({ children }) => <p className="text-sm text-gray-500">{children}</p>;

// Status Badge Component (unchanged)
const StatusBadge = ({ status }) => {
  const statusStyles = {
    'Pending': 'bg-yellow-100 text-yellow-800',
    'In Review': 'bg-blue-100 text-blue-800',
    'Compliant': 'bg-green-100 text-green-800',
    'Non-Compliant': 'bg-red-100 text-red-800',
    'Requires Clarification': 'bg-orange-100 text-orange-800'
  };
  
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${statusStyles[status] || 'bg-gray-100 text-gray-800'}`}>
      {status}
    </span>
  );
};

// Risk Badge Component (unchanged)
const RiskBadge = ({ level }) => {
  const riskStyles = {
    'Low': 'bg-green-100 text-green-800',
    'Medium': 'bg-yellow-100 text-yellow-800',
    'High': 'bg-red-100 text-red-800'
  };
  
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${riskStyles[level] || 'bg-gray-100 text-gray-800'}`}>
      {level}
    </span>
  );
};

// Priority Badge Component (color-coded)
const PriorityBadge = ({ priority }) => {
  let bgColor = 'bg-gray-100 text-gray-800';
  let textColor = 'text-gray-800';
  
  if (priority >= 4) {
    bgColor = 'bg-red-100';
    textColor = 'text-red-800';
  } else if (priority >= 3) {
    bgColor = 'bg-orange-100';
    textColor = 'text-orange-800';
  } else if (priority >= 2) {
    bgColor = 'bg-yellow-100';
    textColor = 'text-yellow-800';
  }
  
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
      {priority >= 4 ? 'High' : priority >= 3 ? 'Medium' : 'Low'}
    </span>
  );
};

export default function ProcurementPlansPage() {
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    riskLevel: "all",
    dateFrom: "",
    dateTo: "",
    sortBy: "priority",
    sortOrder: "desc"
  });
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState("");
  const [complianceData, setComplianceData] = useState({
    status: "",
    remarks: "",
    riskAssessment: "Medium",
    correctiveActions: []
  });
  const [newAction, setNewAction] = useState({ description: "", dueDate: "", assignedTo: "" });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  
  // Refs for click outside handling
  const filterDropdownRef = useRef(null);
  const filterButtonRef = useRef(null);

  // Handle click outside the filter dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      // If the dropdown is open and the click is outside both the dropdown and the button
      if (showFilterDropdown && 
          filterDropdownRef.current && 
          !filterDropdownRef.current.contains(event.target) &&
          filterButtonRef.current &&
          !filterButtonRef.current.contains(event.target)) {
        setShowFilterDropdown(false);
      }
    };

    // Add event listener when dropdown is open
    if (showFilterDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Clean up the event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilterDropdown]);

  // Fetch compliance review plans with filtering and pagination
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        setError("");
        
        const params = {
          page,
          limit,
          sortBy: filters.sortBy,
          sortOrder: filters.sortOrder
        };
        
        if (filters.riskLevel !== "all") params.riskLevel = filters.riskLevel;
        if (filters.dateFrom) params.dateFrom = filters.dateFrom;
        if (filters.dateTo) params.dateTo = filters.dateTo;
        
        const response = await listComplianceReviewPlans(params);
        
        // Handle different response structures
        let plansData = [];
        let paginationData = {};
        
        if (response && response.data) {
          if (response.data.plans) {
            plansData = response.data.plans;
            paginationData = response.data.pagination || {};
          } else if (response.data.data && response.data.data.plans) {
            plansData = response.data.data.plans;
            paginationData = response.data.data.pagination || {};
          } else if (Array.isArray(response.data)) {
            plansData = response.data;
          }
        } else if (Array.isArray(response)) {
          plansData = response;
        } else if (response && response.plans) {
          plansData = response.plans;
          paginationData = response.pagination || {};
        }
        
        setPlans(plansData);
        setTotal(paginationData.total || plansData.length);
      } catch (err) {
        console.error("Error fetching procurement plans:", err);
        setError("Failed to load procurement plans. Please try again.");
        handleApiError(err, setError);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, [page, limit, filters]);

  // Filter plans based on search term
  const filteredPlans = useMemo(() => {
    return plans.filter(plan => {
      return searchTerm === "" || 
        plan.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (plan.projectId && plan.projectId.projectTitle && plan.projectId.projectTitle.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (plan.planReference && plan.planReference.toLowerCase().includes(searchTerm.toLowerCase()));
    });
  }, [plans, searchTerm]);

  // Handle compliance decision
  const handleComplianceDecision = async () => {
    if (!complianceData.status) {
      setActionError("Please select a compliance status");
      return;
    }

    try {
      setActionLoading(true);
      setActionError("");
      
      // Prepare compliance data
      const data = {
        status: complianceData.status,
        remarks: complianceData.remarks,
        riskAssessment: complianceData.riskAssessment
      };
      
      if (complianceData.correctiveActions.length > 0) {
        data.correctiveActions = complianceData.correctiveActions;
      }
      
      // Call API to update compliance status
      await submitComplianceDecision(selectedPlan._id, data);
      
      // Refresh plans list
      const params = {
        page,
        limit,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder
      };
      
      if (filters.riskLevel !== "all") params.riskLevel = filters.riskLevel;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      
      const response = await listComplianceReviewPlans(params);
      
      // Handle different response structures
      let plansData = [];
      if (response && response.data) {
        if (response.data.plans) {
          plansData = response.data.plans;
        } else if (response.data.data && response.data.data.plans) {
          plansData = response.data.data.plans;
        } else if (Array.isArray(response.data)) {
          plansData = response.data;
        }
      } else if (Array.isArray(response)) {
        plansData = response;
      } else if (response && response.plans) {
        plansData = response.plans;
      }
      
      setPlans(plansData);
      
      // Close modal
      setSelectedPlan(null);
      setComplianceData({
        status: "",
        remarks: "",
        riskAssessment: "Medium",
        correctiveActions: []
      });
      
    } catch (err) {
      console.error("Error updating compliance decision:", err);
      setActionError("Failed to update compliance status. Please try again.");
      handleApiError(err, setActionError);
    } finally {
      setActionLoading(false);
    }
  };

  // Add corrective action
  const addCorrectiveAction = () => {
    if (newAction.description.trim() === "") return;
    
    setComplianceData(prev => ({
      ...prev,
      correctiveActions: [...prev.correctiveActions, { ...newAction }]
    }));
    
    setNewAction({ description: "", dueDate: "", assignedTo: "" });
  };

  // Remove corrective action
  const removeCorrectiveAction = (index) => {
    setComplianceData(prev => ({
      ...prev,
      correctiveActions: prev.correctiveActions.filter((_, i) => i !== index)
    }));
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString();
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Calculate days in review
  const getDaysInReview = (plan) => {
    if (!plan.updatedAt) return 0;
    const diffTime = Math.abs(new Date() - new Date(plan.updatedAt));
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      riskLevel: "all",
      dateFrom: "",
      dateTo: "",
      sortBy: "priority",
      sortOrder: "desc"
    });
  };

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return filters.riskLevel !== "all" || filters.dateFrom || filters.dateTo;
  }, [filters]);

  return (
    <div className="flex bg-gray-50 min-h-screen">
      {/* Sidebar */}
      <HOSidebar
        visible={sidebarVisible} 
        onClose={() => setSidebarVisible(false)} 
        className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100" 
      />
      
      {/* Main Content Area */}
      <div className="flex-1 md:ml-64 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
        {/* Top Navigation */}
        <Topbar onSidebarToggle={() => setSidebarVisible(true)} />
        
        {/* Main Content */}
        <main className="mt-16 p-4 md:p-6 space-y-6 h-[calc(100vh-4rem)] overflow-auto">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Procurement Compliance Dashboard</h1>
              <p className="text-gray-600 mt-1">Review and approve procurement plans requiring compliance validation</p>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {/* Search and Filters */}
          <Card>
            <CardContent>
              <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center w-full lg:w-auto">
                  <div className="relative flex-1 sm:flex-initial">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Search plans..."
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 w-full sm:w-80 text-gray-900"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  
                  {/* Filter Dropdown */}
                  <div className="relative">
                    <button
                      ref={filterButtonRef}
                      onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                      className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <Filter className="w-5 h-5" />
                      <span>Filters</span>
                      <ChevronDown className={`w-4 h-4 transition-transform ${showFilterDropdown ? 'rotate-180' : ''}`} />
                      {hasActiveFilters && (
                        <span className="absolute -top-1 -right-1 w-3 h-3 bg-orange-500 rounded-full"></span>
                      )}
                    </button>
                    
                    {showFilterDropdown && (
                      <div 
                        ref={filterDropdownRef}
                        className="absolute z-10 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg"
                      >
                        <div className="p-4 space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Risk Level</label>
                            <select
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                              value={filters.riskLevel}
                              onChange={(e) => handleFilterChange('riskLevel', e.target.value)}
                            >
                              <option value="all">All Risks</option>
                              <option value="Low">Low Risk</option>
                              <option value="Medium">Medium Risk</option>
                              <option value="High">High Risk</option>
                            </select>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Date Range (When plans were created)
                            </label>
                            <div className="space-y-2">
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">From Date (start of range)</label>
                                <input
                                  type="date"
                                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                  value={filters.dateFrom}
                                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                                  placeholder="Select start date"
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">To Date (end of range)</label>
                                <input
                                  type="date"
                                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                  value={filters.dateTo}
                                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                                  placeholder="Select end date"
                                />
                              </div>
                            </div>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
                            <select
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                              value={filters.sortBy}
                              onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                            >
                              <option value="priority">Priority</option>
                              <option value="createdAt">Date Created</option>
                              <option value="updatedAt">Last Updated</option>
                            </select>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
                            <select
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                              value={filters.sortOrder}
                              onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
                            >
                              <option value="desc">Newest First</option>
                              <option value="asc">Oldest First</option>
                            </select>
                          </div>
                          
                          <div className="flex justify-between pt-2 border-t">
                            <button
                              onClick={clearFilters}
                              className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                            >
                              Clear All
                            </button>
                            <button
                              onClick={() => setShowFilterDropdown(false)}
                              className="px-3 py-1 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                            >
                              Apply Filters
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-sm font-medium text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
                  {filteredPlans.length} of {total} plans
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Procurement Plans Table */}
          <Card>
            <CardHeader className="bg-orange-50">
              <CardTitle>Procurement Plans Requiring Compliance Review</CardTitle>
              <CardDescription>Plans that need your review and approval</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-orange-500"></div>
                </div>
              ) : filteredPlans.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No procurement plans found</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {searchTerm || hasActiveFilters
                      ? "Try adjusting your filters" 
                      : "All procurement plans have been reviewed"}
                  </p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan Reference</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Risk Level</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Days in Review</th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredPlans.map((plan) => (
                          <tr key={plan._id} className="hover:bg-gray-50 transition-colors duration-150">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{plan.planReference || "-"}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <PriorityBadge priority={plan.priority || 0} />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{plan.projectId?.projectTitle || "-"}</div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm font-medium text-gray-900">{plan.title}</div>
                              <div className="text-xs text-gray-500">{plan.procurementMethod}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <RiskBadge level={plan.complianceReview?.riskAssessment || "Low"} />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <div className="flex items-center">
                                <Clock className="h-4 w-4 mr-1 text-orange-500" />
                                {getDaysInReview(plan)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                              <button
                                onClick={() => {
                                  setSelectedPlan(plan);
                                  setComplianceData({
                                    status: "",
                                    remarks: "",
                                    riskAssessment: plan.complianceReview?.riskAssessment || "Medium",
                                    correctiveActions: []
                                  });
                                }}
                                className="px-3 py-1 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors duration-150"
                              >
                                Review
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {total > limit && (
                    <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 sm:px-6 mt-4">
                      <div className="flex flex-1 justify-between sm:hidden">
                        <button
                          onClick={() => handlePageChange(page - 1)}
                          disabled={page === 1}
                          className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => handlePageChange(page + 1)}
                          disabled={page >= Math.ceil(total / limit)}
                          className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                        >
                          Next
                        </button>
                      </div>
                      <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                        <div>
                          <p className="text-sm text-gray-700">
                            Showing <span className="font-medium">{(page - 1) * limit + 1}</span> to{" "}
                            <span className="font-medium">{Math.min(page * limit, total)}</span> of{" "}
                            <span className="font-medium">{total}</span> results
                          </p>
                        </div>
                        <div>
                          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                            <button
                              onClick={() => handlePageChange(page - 1)}
                              disabled={page === 1}
                              className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                            >
                              <span className="sr-only">Previous</span>
                              &larr;
                            </button>
                            <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                              Page {page} of {Math.ceil(total / limit)}
                            </span>
                            <button
                              onClick={() => handlePageChange(page + 1)}
                              disabled={page >= Math.ceil(total / limit)}
                              className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                            >
                              <span className="sr-only">Next</span>
                              &rarr;
                            </button>
                          </nav>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Plan Details Modal */}
          {selectedPlan && (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setSelectedPlan(null)} />
              <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                <div className="bg-orange-100 p-4 border-b flex items-center justify-between">
                  <h3 className="font-semibold text-lg">Procurement Plan Details</h3>
                  <button onClick={() => setSelectedPlan(null)} className="p-1 hover:bg-orange-200 rounded">
                    <X className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Plan Information</h4>
                      <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Reference:</span>
                          <span className="font-medium">{selectedPlan.planReference || "-"}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Title:</span>
                          <span className="font-medium">{selectedPlan.title}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Method:</span>
                          <span className="font-medium">{selectedPlan.procurementMethod}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Status:</span>
                          <StatusBadge status={selectedPlan.complianceReview?.status || "Pending"} />
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Risk Level:</span>
                          <RiskBadge level={selectedPlan.complianceReview?.riskAssessment || "Low"} />
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Priority:</span>
                          <PriorityBadge priority={selectedPlan.priority || 0} />
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Project Information</h4>
                      <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Project:</span>
                          <span className="font-medium">{selectedPlan.projectId?.projectTitle || "-"}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Start Date:</span>
                          <span className="font-medium">{formatDate(selectedPlan.startDate)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">End Date:</span>
                          <span className="font-medium">{formatDate(selectedPlan.endDate)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Days in Review:</span>
                          <span className="font-medium">{getDaysInReview(selectedPlan)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <h4 className="font-medium text-gray-900 mb-2">Financial Summary</h4>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="text-center">
                          <div className="text-sm text-gray-500">Subtotal</div>
                          <div className="font-medium">${selectedPlan.financials?.subtotal?.toLocaleString() || "0"}</div>
                        </div>
                        <div className="text-center">
                          <div className="text-sm text-gray-500">Taxes</div>
                          <div className="font-medium">${selectedPlan.financials?.taxes?.toLocaleString() || "0"}</div>
                        </div>
                        <div className="text-center">
                          <div className="text-sm text-gray-500">Contingency</div>
                          <div className="font-medium">${selectedPlan.financials?.contingency?.toLocaleString() || "0"}</div>
                        </div>
                        <div className="text-center">
                          <div className="text-sm text-gray-500">Grand Total</div>
                          <div className="font-medium">${selectedPlan.financials?.grandTotal?.toLocaleString() || "0"}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <h4 className="font-medium text-gray-900 mb-2">Compliance History</h4>
                    <div className="bg-gray-50 rounded-lg p-4">
                      {selectedPlan.complianceHistory && selectedPlan.complianceHistory.length > 0 ? (
                        <div className="space-y-3">
                          {selectedPlan.complianceHistory.map((history, index) => (
                            <div key={index} className="border-b border-gray-200 pb-3 last:border-0 last:pb-0">
                              <div className="flex justify-between">
                                <StatusBadge status={history.status} />
                                <span className="text-sm text-gray-500">{formatDate(history.reviewedAt)}</span>
                              </div>
                              <div className="mt-1 text-sm">
                                <span className="text-gray-500">Risk: </span>
                                <RiskBadge level={history.riskAssessment || "Low"} />
                              </div>
                              {history.remarks && (
                                <div className="mt-1 text-sm">
                                  <span className="text-gray-500">Remarks: </span>
                                  <span>{history.remarks}</span>
                                </div>
                              )}
                              {history.reviewedBy && (
                                <div className="mt-1 text-sm text-gray-500">
                                  Reviewed by: {history.reviewedBy.firstName} {history.reviewedBy.lastName}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">No compliance history available</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <h4 className="font-medium text-gray-900 mb-2">Compliance Review</h4>
                    <div className="bg-gray-50 rounded-lg p-4">
                      {actionError && (
                        <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
                          <p className="text-red-800">{actionError}</p>
                        </div>
                      )}
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Compliance Status *
                          </label>
                          <select
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            value={complianceData.status}
                            onChange={(e) => setComplianceData({...complianceData, status: e.target.value})}
                          >
                            <option value="">Select a status</option>
                            <option value="Compliant">Compliant</option>
                            <option value="Non-Compliant">Non-Compliant</option>
                            <option value="Requires Clarification">Requires Clarification</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Risk Assessment
                          </label>
                          <select
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            value={complianceData.riskAssessment}
                            onChange={(e) => setComplianceData({...complianceData, riskAssessment: e.target.value})}
                          >
                            <option value="Low">Low</option>
                            <option value="Medium">Medium</option>
                            <option value="High">High</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Remarks
                          </label>
                          <textarea
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            rows={3}
                            placeholder="Enter your remarks about this compliance decision..."
                            value={complianceData.remarks}
                            onChange={(e) => setComplianceData({...complianceData, remarks: e.target.value})}
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Corrective Actions
                          </label>
                          <div className="space-y-2">
                            {complianceData.correctiveActions.map((action, index) => (
                              <div key={index} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-3">
                                <div>
                                  <div className="font-medium">{action.description}</div>
                                  <div className="text-sm text-gray-500">
                                    Due: {action.dueDate ? formatDate(action.dueDate) : "Not set"} | 
                                    Assigned: {action.assignedTo || "Not assigned"}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => removeCorrectiveAction(index)}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  <X className="h-5 w-5" />
                                </button>
                              </div>
                            ))}
                            
                            <div className="flex flex-col sm:flex-row gap-2 mt-2">
                              <input
                                type="text"
                                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                placeholder="Action description"
                                value={newAction.description}
                                onChange={(e) => setNewAction({...newAction, description: e.target.value})}
                              />
                              <input
                                type="date"
                                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                value={newAction.dueDate}
                                onChange={(e) => setNewAction({...newAction, dueDate: e.target.value})}
                              />
                              <input
                                type="text"
                                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                placeholder="Assigned to"
                                value={newAction.assignedTo}
                                onChange={(e) => setNewAction({...newAction, assignedTo: e.target.value})}
                              />
                              <button
                                type="button"
                                onClick={addCorrectiveAction}
                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
                              >
                                Add
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      onClick={() => setSelectedPlan(null)}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleComplianceDecision}
                      disabled={actionLoading || !complianceData.status}
                      className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
                    >
                      {actionLoading ? "Processing..." : "Submit Decision"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}